/**
 * CampaignBriefing – Formulário de briefing para contexto IA.
 * Reutilizável no editor de e-mail, LaunchWizard, FlowTemplateGallery.
 *
 * Campos:
 *  - Produto (1 produto)
 *  - Públicos-alvo (múltiplos via popover de checkboxes)
 *  - Estilo de copy (LightCopy / Agência / Launch / Padrão)
 *  - Notas adicionais
 */

import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, Users } from "lucide-react";
import { trpc } from "@/lib/trpc";
import ProductSelector from "./ProductSelector";

export type CopyStyle = "lightcopy" | "agencia" | "launch" | "default";

export interface BriefingData {
  productId?: number;
  /** primeiro id é mantido em `audienceId` por compat; novos consumidores devem usar audienceIds */
  audienceId?: number;
  audienceIds?: number[];
  copyStyle?: CopyStyle;
  notes?: string;
}

const STYLE_OPTIONS: { value: CopyStyle; label: string; hint: string }[] = [
  { value: "lightcopy", label: "LightCopy (sincero)", hint: "Conversacional, premissa > promessa" },
  { value: "agencia",   label: "Agência de Copy",     hint: "Gancho → Pitch → Objeções → CTA" },
  { value: "launch",    label: "Lançamento (Walker)", hint: "Pré-pré, PLCs, carrinho, pós" },
  { value: "default",   label: "Padrão (clareza)",    hint: "Direto, sem viés de estilo" },
];

interface CampaignBriefingProps {
  value: BriefingData;
  onChange: (data: BriefingData) => void;
  compact?: boolean;
}

function MultiAudiencePicker({
  selectedIds,
  onChange,
}: {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const { data: audiences = [] } = trpc.aiEmail.listAudiences.useQuery();
  const active = (audiences as Array<{ id: number; name: string; role?: string | null; isActive: boolean }>)
    .filter((a) => a.isActive);

  const toggle = (id: number) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  const label = selectedIds.length === 0
    ? "Selecionar públicos…"
    : `${selectedIds.length} público${selectedIds.length === 1 ? "" : "s"} selecionado${selectedIds.length === 1 ? "" : "s"}`;

  return (
    <div className="space-y-1.5">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 w-full justify-start font-normal text-sm">
            <Users className="w-3.5 h-3.5 text-muted-foreground mr-2" />
            <span className="truncate">{label}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[calc(100vw-2rem)] sm:w-72 p-2">
          <ScrollArea className="h-56">
            {active.length === 0 ? (
              <p className="text-xs text-muted-foreground p-2 text-center">Nenhum público cadastrado.</p>
            ) : (
              active.map((a) => (
                <label key={a.id} className="flex items-center gap-2 px-1 py-1.5 text-xs cursor-pointer hover:bg-muted rounded">
                  <Checkbox checked={selectedIds.includes(a.id)} onCheckedChange={() => toggle(a.id)} />
                  <span className="flex-1 truncate">{a.name}</span>
                  {a.role && <span className="text-[10px] text-muted-foreground">({a.role})</span>}
                </label>
              ))
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedIds.map((id) => {
            const a = active.find((x) => x.id === id);
            if (!a) return null;
            return (
              <Badge key={id} variant="secondary" className="text-[10px] gap-1 max-w-[200px]">
                <span className="truncate">{a.name}</span>
                <button onClick={() => toggle(id)} className="ml-0.5 hover:text-destructive">×</button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CampaignBriefing({ value, onChange, compact }: CampaignBriefingProps) {
  const update = (partial: Partial<BriefingData>) => {
    const next = { ...value, ...partial };
    // Sincroniza audienceId com o primeiro de audienceIds (compat backend single)
    if (partial.audienceIds !== undefined) {
      next.audienceId = partial.audienceIds[0];
    }
    onChange(next);
  };

  const audienceIds = value.audienceIds ?? (value.audienceId ? [value.audienceId] : []);
  const style = value.copyStyle ?? "lightcopy";

  const Wrapper = compact ? "div" : Card;
  const wrapperProps = compact
    ? { className: "space-y-3" }
    : { className: "p-4 space-y-4 border-indigo-200 bg-indigo-50/30" };

  return (
    <Wrapper {...wrapperProps}>
      {!compact && (
        <>
          <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700">
            <Sparkles className="w-4 h-4" />
            Briefing IA
          </div>
          <p className="text-xs text-muted-foreground">
            Defina contexto pra IA gerar conteúdo alinhado ao seu negócio.
          </p>
        </>
      )}

      {/* Layout em 1 coluna pra evitar overflow dos selectors em modais estreitos */}
      <div>
        <Label className={compact ? "text-xs" : ""}>Produto / Oferta</Label>
        <ProductSelector value={value.productId} onChange={(id) => update({ productId: id })} />
      </div>

      <div>
        <Label className={compact ? "text-xs" : ""}>Públicos-Alvo</Label>
        <MultiAudiencePicker selectedIds={audienceIds} onChange={(ids) => update({ audienceIds: ids })} />
      </div>

      <div>
        <Label className={compact ? "text-xs" : ""}>Estilo de copy</Label>
        <Select value={style} onValueChange={(v) => update({ copyStyle: v as CopyStyle })}>
          <SelectTrigger className={compact ? "h-9 text-sm" : ""}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STYLE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{opt.label}</span>
                  <span className="text-[10px] text-muted-foreground">{opt.hint}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className={compact ? "text-xs" : ""}>Notas adicionais</Label>
        <Textarea
          value={value.notes ?? ""}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder={compact ? "Contexto extra para a IA…" : "Informações adicionais, tom desejado, referências, contexto do momento…"}
          rows={compact ? 2 : 3}
          className={compact ? "text-xs" : ""}
        />
      </div>
    </Wrapper>
  );
}
